import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Plus } from "lucide-react";
import { staffClient } from "clients/staff";
import { StaffApis } from "apis/staff";
import Table, { Column } from "ui/Table";
import Modal from "ui/Modal";
import Input from "ui/Input";
import Select from "ui/Select";
import Button from "ui/Button";
import Loading from "ui/Loading";
import PageContainer from "ui/PageContainer";
import PageHeader from "ui/PageHeader";
import FilterSection from "ui/FilterSection";
import TableContainer from "ui/TableContainer";
import SearchInput from "ui/SearchInput";
import { Badge } from "ui/shadcn";
import { toast } from "react-toastify";

interface Staff {
    id: number;
    user_id: string;
    name: string;
    user_type: 'STAFF' | 'MASTER';
    phone_number?: string;
    password_reset_required?: boolean;
    created_at?: string;
    updated_at?: string;
}

const USER_TYPE_OPTIONS = [
    { value: "STAFF", label: "직원" },
    { value: "MASTER", label: "관리자" }
];

const StaffsPage = () => {
    const navigate = useNavigate();
    const [staffs, setStaffs] = useState<Staff[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Form states
    const [formData, setFormData] = useState({
        user_id: "",
        name: "",
        user_type: "STAFF" as 'STAFF' | 'MASTER',
        password_reset_required: false
    });

    // Fetch staffs
    const fetchStaffs = async () => {
        try {
            setLoading(true);
            const ret = await staffClient.get('/staffs', {
                params: { limit: 1000 }
            });
            setStaffs(ret.data.data || []);
        } catch (error) {
            console.error("Failed to fetch staffs:", error);
            toast.error("직원 목록을 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaffs();
    }, []);

    // Table columns
    const columns: Column<Staff>[] = [
        {
            key: 'id',
            header: 'ID',
            width: '60px',
            render: (value) => <span className="tabular-nums text-muted-foreground">{value}</span>
        },
        {
            key: 'user_id',
            header: '사용자 ID',
            width: '150px',
        },
        {
            key: 'name',
            header: '이름',
            width: '120px',
        },
        {
            key: 'user_type',
            header: '회원 유형',
            width: '100px',
            render: (value) => (
                <Badge variant={value === 'MASTER' ? 'default' : 'secondary'}>
                    {value === 'MASTER' ? '관리자' : '직원'}
                </Badge>
            )
        },
        {
            key: 'phone_number',
            header: '휴대폰번호',
            width: '140px',
            render: (value) => value ? <span className="tabular-nums">{value}</span> : '-'
        },
        {
            key: 'password_reset_required',
            header: '비밀번호 재설정',
            width: '140px',
            render: (value) => (
                <Badge variant={value ? 'warning' : 'success'}>
                    {value ? (
                        <AlertTriangle className="h-3 w-3" strokeWidth={1.8} />
                    ) : (
                        <CheckCircle2 className="h-3 w-3" strokeWidth={1.8} />
                    )}
                    {value ? '재설정 필요' : '완료'}
                </Badge>
            )
        }
    ];

    // Filtered data
    const filteredStaffs = staffs.filter(staff => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            return staff.user_id.toLowerCase().includes(search) ||
                   staff.name.toLowerCase().includes(search);
        }
        return true;
    });

    // Create staff
    const handleCreate = async () => {
        try {
            await staffClient.post('/staffs', formData);
            toast.success("직원이 추가되었습니다.");
            setShowCreateModal(false);
            fetchStaffs();
            resetForm();
        } catch (error: any) {
            console.error("Failed to create staff:", error);
            toast.error(error.response?.data?.message || "직원 추가에 실패했습니다.");
        }
    };

    // Edit staff
    const handleEdit = async () => {
        if (!selectedStaff) return;

        try {
            // Handle password reset if needed
            if (formData.password_reset_required && !selectedStaff.password_reset_required) {
                await StaffApis.resetPassword(selectedStaff.user_id);
            }

            await staffClient.patch(`/staffs/${selectedStaff.id}`, {
                name: formData.name,
                user_type: formData.user_type,
                password_reset_required: formData.password_reset_required
            });

            toast.success("직원 정보가 수정되었습니다.");
            setShowEditModal(false);
            fetchStaffs();
            resetForm();
        } catch (error: any) {
            console.error("Failed to edit staff:", error);
            toast.error(error.response?.data?.message || "직원 수정에 실패했습니다.");
        }
    };

    // Delete staff
    const handleDelete = async (id: number) => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;

        try {
            await staffClient.delete(`/staffs/${id}`);
            toast.success("직원이 삭제되었습니다.");
            fetchStaffs();
        } catch (error: any) {
            console.error("Failed to delete staff:", error);
            toast.error(error.response?.data?.message || "직원 삭제에 실패했습니다.");
        }
    };

    // Open edit modal
    const openEditModal = (staff: Staff) => {
        setSelectedStaff(staff);
        setFormData({
            user_id: staff.user_id,
            name: staff.name,
            user_type: staff.user_type,
            password_reset_required: staff.password_reset_required || false
        });
        setShowEditModal(true);
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            user_id: "",
            name: "",
            user_type: "STAFF",
            password_reset_required: false
        });
        setSelectedStaff(null);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        resetForm();
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        resetForm();
    };

    if (loading) {
        return (
            <PageContainer>
                <Loading text="직원 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="직원 관리"
                subtitle="직원 계정 및 권한 관리"
                actions={
                    <Button variant="primary" icon={<Plus />} onClick={() => setShowCreateModal(true)}>
                        직원 추가
                    </Button>
                }
            />

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="사용자 ID 또는 이름으로 검색..."
                />
            </FilterSection>

            <TableContainer
                title="직원 목록"
                resultCount={filteredStaffs.length}
            >
                <Table
                    columns={columns}
                    data={filteredStaffs}
                    pagination
                    pageSize={20}
                    stickyHeader
                    maxHeight="600px"
                    emptyMessage="등록된 직원이 없습니다."
                    onRowClick={(staff) => navigate(`/admin/staffs/${staff.id}`)}
                    actionsColumnWidth="150px"
                    actions={(staff) => (
                        <>
                            <Button
                                size="small"
                                variant="outline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(staff);
                                }}
                            >
                                계정 관리
                            </Button>
                            <Button
                                size="small"
                                variant="danger-outline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(staff.id);
                                }}
                            >
                                삭제
                            </Button>
                        </>
                    )}
                />
            </TableContainer>

            {/* Create Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={closeCreateModal}
                title="직원 추가"
                footer={
                    <>
                        <Button variant="outline" onClick={closeCreateModal}>
                            취소
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleCreate}
                            disabled={!formData.user_id || !formData.name}
                        >
                            추가
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="사용자 ID"
                        required
                        type="text"
                        value={formData.user_id}
                        onChange={(e) => setFormData({...formData, user_id: e.target.value})}
                        placeholder="영문, 숫자 조합"
                    />
                    <Input
                        label="이름"
                        required
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="직원 이름"
                    />
                    <Select
                        label="회원 유형"
                        required
                        value={formData.user_type}
                        onChange={(e) => setFormData({...formData, user_type: e.target.value as 'STAFF' | 'MASTER'})}
                        options={USER_TYPE_OPTIONS}
                    />
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={closeEditModal}
                title="계정 관리"
                footer={
                    <>
                        <Button variant="outline" onClick={closeEditModal}>
                            취소
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleEdit}
                            disabled={!formData.name}
                        >
                            수정
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="사용자 ID"
                        type="text"
                        value={formData.user_id}
                        disabled
                    />
                    <Input
                        label="이름"
                        required
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                    <Select
                        label="회원 유형"
                        required
                        value={formData.user_type}
                        onChange={(e) => setFormData({...formData, user_type: e.target.value as 'STAFF' | 'MASTER'})}
                        options={USER_TYPE_OPTIONS}
                    />
                    <Select
                        label="비밀번호 재설정 필요"
                        value={formData.password_reset_required ? "true" : "false"}
                        onChange={(e) => setFormData({...formData, password_reset_required: e.target.value === "true"})}
                        options={[
                            { value: "false", label: "완료" },
                            { value: "true", label: "재설정 필요" }
                        ]}
                    />
                </div>
            </Modal>
        </PageContainer>
    );
};

export default StaffsPage;
